import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { formatCurrency, formatDateShort } from '../utils/format';
import { 
  FileText, Plus, Edit2, Trash2, Upload, Eye, X, 
  Calendar, CheckCircle, RefreshCw, RotateCcw, DollarSign, TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LC() {
  const { currentUser } = useApp();
  const [lcler, setLcler] = useState([]);
  const [bankalar, setBankalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLC, setEditingLC] = useState(null);
  const [showSilinmisler, setShowSilinmisler] = useState(false);
  const [ozet, setOzet] = useState(null);
  const [kurlar, setKurlar] = useState({ USD: 34.50, EUR: 37.50 });
  
  // Form state
  const [formData, setFormData] = useState({
    lcAdi: '',
    firma: '',
    bankaAdi: '',
    tutarUSD: '',
    odemeTarihi: '',
    gelisTarihi: ''
  });

  useEffect(() => {
    loadData();
  }, [showSilinmisler]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [lcRes, bankalarRes, ozetRes, kurRes] = await Promise.all([
        api.getLCOdemeler({ silinmisler: showSilinmisler }),
        api.getBankalar(),
        api.getLCOzet(),
        api.getDovizKuru()
      ]);
      setLcler(lcRes.data);
      setBankalar(bankalarRes.data);
      setOzet(ozetRes.data);
      setKurlar({
        USD: kurRes.data.USD || 34.50,
        EUR: kurRes.data.EUR || 37.50
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      lcAdi: '',
      firma: '',
      bankaAdi: '',
      tutarUSD: '',
      odemeTarihi: '',
      gelisTarihi: ''
    });
    setEditingLC(null);
  };

  const openModal = (lc = null) => {
    if (lc) {
      setEditingLC(lc);
      setFormData({
        lcAdi: lc.lcAdi || '',
        firma: lc.firma || '',
        bankaAdi: lc.bankaAdi || '',
        tutarUSD: lc.tutarUSD?.toString() || '',
        odemeTarihi: lc.odemeTarihi || '',
        gelisTarihi: lc.gelisTarihi || ''
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.lcAdi || !formData.firma || !formData.bankaAdi || !formData.tutarUSD || !formData.odemeTarihi) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    try {
      const data = {
        lcAdi: formData.lcAdi,
        firma: formData.firma,
        bankaAdi: formData.bankaAdi,
        tutarUSD: parseFloat(formData.tutarUSD),
        odemeTarihi: formData.odemeTarihi,
        gelisTarihi: formData.gelisTarihi || null,
        girenKullaniciId: currentUser?.id
      };

      if (editingLC) {
        await api.updateLCOdeme(editingLC.id, data);
        toast.success('LC ödemesi güncellendi');
      } else {
        await api.createLCOdeme(data);
        toast.success('LC ödemesi eklendi');
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (lc) => {
    if (!window.confirm(`"${lc.lcAdi}" LC ödemesini silmek istediğinize emin misiniz?`)) return;
    
    try {
      await api.deleteLCOdeme(lc.id, currentUser?.id);
      toast.success('LC ödemesi silindi');
      loadData();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleGeriYukle = async (lc) => {
    try {
      await api.geriYukleLCOdeme(lc.id);
      toast.success('LC ödemesi geri yüklendi');
      loadData();
    } catch (error) {
      toast.error('Geri yükleme başarısız');
    }
  };

  const handleOde = async (lc) => {
    if (!window.confirm(`"${lc.lcAdi}" LC ödemesini ödenmiş olarak işaretlemek istiyor musunuz?`)) return;
    
    try {
      await api.odeLCOdeme(lc.id);
      toast.success('LC ödemesi ödendi olarak işaretlendi');
      loadData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleUploadProforma = async (lcId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.uploadLCProforma(lcId, formData);
      toast.success('Proforma yüklendi');
      loadData();
    } catch (error) {
      toast.error('Proforma yüklenemedi');
    }
  };

  // USD to TRY conversion
  const convertToTRY = (usd) => {
    return (usd || 0) * kurlar.USD;
  };

  const getDurumBadge = (durum) => {
    if (durum === 'odendi') {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 border border-green-300 rounded flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Ödendi
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300 rounded">
        Beklemede
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="lc-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-textPrimary flex items-center gap-2">
            <FileText className="h-5 w-5 md:h-6 md:w-6" />
            LC Ödemeleri (Akreditif)
          </h1>
          <p className="text-xs md:text-sm text-textSecondary mt-1">
            USD cinsinden akreditif ödemelerinizi yönetin
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showSilinmisler ? "default" : "outline"}
            onClick={() => setShowSilinmisler(!showSilinmisler)}
            size="sm"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {showSilinmisler ? 'Aktif Ödemeler' : 'Silinenler'}
          </Button>
          <Button onClick={() => openModal()} className="bg-primary" data-testid="yeni-lc-btn">
            <Plus className="h-4 w-4 mr-1" />
            Yeni LC
          </Button>
        </div>
      </div>

      {/* Döviz Kurları */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-3 md:p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 md:h-5 md:w-5" />
            <span className="text-sm md:font-medium">Güncel Döviz Kurları</span>
          </div>
          <div className="flex gap-4 md:gap-6 text-sm">
            <div className="flex items-center gap-1 md:gap-2">
              <DollarSign className="h-3 w-3 md:h-4 md:w-4" />
              <span className="font-bold">USD/TRY: {kurlar.USD?.toFixed(4)}</span>
            </div>
            <div className="flex items-center gap-1 md:gap-2">
              <span className="font-bold">EUR/TRY: {kurlar.EUR?.toFixed(4)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Özet Kartları */}
      {ozet && !showSilinmisler && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-surface border border-border rounded-lg p-3 md:p-4">
            <p className="text-xs text-textSecondary">Bekleyen Toplam (USD)</p>
            <p className="text-base md:text-xl font-bold text-blue-600">${ozet.toplamUSD?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3 md:p-4">
            <p className="text-xs text-textSecondary">Bekleyen Toplam (TRY)</p>
            <p className="text-base md:text-xl font-bold text-green-600">{formatCurrency(ozet.toplamTRY)}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3 md:p-4">
            <p className="text-xs text-textSecondary">Kullanılan Kur</p>
            <p className="text-base md:text-xl font-bold text-amber-600">{ozet.usdKur?.toFixed(4)}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-3 md:p-4">
            <p className="text-xs text-textSecondary">Bekleyen LC Sayısı</p>
            <p className="text-base md:text-xl font-bold text-purple-600">{ozet.lcSayisi}</p>
          </div>
        </div>
      )}

      {/* LC Listesi */}
      {lcler.length === 0 ? (
        <div className="text-center py-12 bg-surface border border-border rounded-lg">
          <FileText className="h-12 w-12 mx-auto text-textSecondary mb-4" />
          <p className="text-textSecondary">
            {showSilinmisler ? 'Silinmiş LC ödemesi bulunmuyor' : 'Henüz LC ödemesi eklenmemiş'}
          </p>
          {!showSilinmisler && (
            <Button onClick={() => openModal()} className="mt-4 bg-primary">
              <Plus className="h-4 w-4 mr-1" />
              İlk LC Ekle
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="lc-table">
              <thead className="bg-surfaceSubtle border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">LC Adı</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Firma</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Banka</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">Tutar (USD)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">Tutar (TRY)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Ödeme Tarihi</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Geliş Tarihi</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-textSecondary uppercase">Durum</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-textSecondary uppercase">Proforma</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-textSecondary uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lcler.map((lc) => (
                  <tr key={lc.id} className={`hover:bg-surfaceSubtle/50 ${showSilinmisler ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3 font-medium text-textPrimary">{lc.lcAdi}</td>
                    <td className="px-4 py-3 text-textSecondary">{lc.firma}</td>
                    <td className="px-4 py-3 text-textSecondary">{lc.bankaAdi}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-blue-600">
                      ${lc.tutarUSD?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-green-600">
                      {formatCurrency(convertToTRY(lc.tutarUSD))}
                    </td>
                    <td className="px-4 py-3 text-textPrimary">{formatDateShort(lc.odemeTarihi)}</td>
                    <td className="px-4 py-3 text-textSecondary">{lc.gelisTarihi ? formatDateShort(lc.gelisTarihi) : '-'}</td>
                    <td className="px-4 py-3 text-center">{getDurumBadge(lc.durum)}</td>
                    <td className="px-4 py-3 text-center">
                      {lc.proformaUrl ? (
                        <a
                          href={`${BACKEND_URL}${lc.proformaUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline flex items-center justify-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                        </a>
                      ) : (
                        <label className="cursor-pointer text-textSecondary hover:text-primary">
                          <Upload className="h-4 w-4 mx-auto" />
                          <input
                            type="file"
                            className="hidden"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => e.target.files[0] && handleUploadProforma(lc.id, e.target.files[0])}
                          />
                        </label>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {showSilinmisler ? (
                          <Button size="sm" variant="ghost" onClick={() => handleGeriYukle(lc)} title="Geri Yükle">
                            <RotateCcw className="h-4 w-4 text-green-500" />
                          </Button>
                        ) : (
                          <>
                            {lc.durum === 'beklemede' && (
                              <Button size="sm" variant="ghost" onClick={() => handleOde(lc)} title="Ödendi İşaretle">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => openModal(lc)} title="Düzenle">
                              <Edit2 className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(lc)} title="Sil">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Genel Toplam */}
      {lcler.length > 0 && !showSilinmisler && (
        <div className="bg-primary/10 border-2 border-primary rounded-lg p-3 md:p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 md:gap-4">
          <div>
            <p className="text-xs md:text-sm text-textSecondary">Toplam LC Ödemeleri</p>
            <p className="text-xs text-textSecondary mt-1">{lcler.length} adet LC</p>
          </div>
          <div className="flex gap-4 md:gap-8">
            <div>
              <p className="text-xs text-textSecondary">USD</p>
              <p className="text-lg md:text-2xl font-mono font-bold text-blue-600">
                ${lcler.reduce((sum, lc) => sum + (lc.tutarUSD || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-xs text-textSecondary">TRY</p>
              <p className="text-lg md:text-2xl font-mono font-bold text-green-600">
                {formatCurrency(lcler.reduce((sum, lc) => sum + convertToTRY(lc.tutarUSD), 0))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-bold">
                {editingLC ? 'LC Ödemesi Düzenle' : 'Yeni LC Ödemesi'}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">LC Adı / Açıklama *</label>
                <Input
                  value={formData.lcAdi}
                  onChange={(e) => setFormData({ ...formData, lcAdi: e.target.value })}
                  placeholder="Örn: LC-2024-001"
                  data-testid="lc-adi-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Firma *</label>
                <Input
                  value={formData.firma}
                  onChange={(e) => setFormData({ ...formData, firma: e.target.value })}
                  placeholder="Firma adı"
                  data-testid="lc-firma-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Banka *</label>
                <Select
                  value={formData.bankaAdi}
                  onValueChange={(val) => setFormData({ ...formData, bankaAdi: val })}
                >
                  <SelectTrigger data-testid="lc-banka-select">
                    <SelectValue placeholder="Banka seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...new Set(bankalar.map(b => b.ad))].map((ad) => (
                      <SelectItem key={ad} value={ad}>{ad}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Tutar (USD) *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
                  <Input
                    type="number"
                    value={formData.tutarUSD}
                    onChange={(e) => setFormData({ ...formData, tutarUSD: e.target.value })}
                    placeholder="10000"
                    className="pl-9"
                    data-testid="lc-tutar-input"
                  />
                </div>
                {formData.tutarUSD && (
                  <p className="text-xs text-textSecondary mt-1">
                    TRY Karşılığı: {formatCurrency(parseFloat(formData.tutarUSD) * kurlar.USD)}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Ödeme Tarihi *</label>
                  <Input
                    type="date"
                    value={formData.odemeTarihi}
                    onChange={(e) => setFormData({ ...formData, odemeTarihi: e.target.value })}
                    data-testid="lc-odeme-tarihi-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Geliş Tarihi</label>
                  <Input
                    type="date"
                    value={formData.gelisTarihi}
                    onChange={(e) => setFormData({ ...formData, gelisTarihi: e.target.value })}
                    data-testid="lc-gelis-tarihi-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2 p-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                İptal
              </Button>
              <Button onClick={handleSubmit} className="flex-1 bg-primary" data-testid="lc-kaydet-btn">
                {editingLC ? 'Güncelle' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
